import { motion } from "framer-motion";
import { Shield, Truck, Building2, Wrench, Plane, Anchor } from "lucide-react";

const services = [
  {
    n: "01",
    key: "security",
    icon: Shield,
    title: "Executive & Convoy Security",
    tag: "Iraq · MENA",
    body:
      "Close-protection details, convoy movements, and static site security for Americans and their partners operating in Iraq. Vetted operators, quiet execution, full accountability.",
    img: "https://images.unsplash.com/photo-1661113248118-34162f1c6eac?auto=format&fit=crop&w=1400&q=70",
    bullets: ["Close protection (PSD)", "Convoy & route security", "Static & residential", "Risk consulting & K9"],
  },
  {
    n: "02",
    key: "life_support",
    icon: Truck,
    title: "Life Support & Logistics",
    tag: "Bases · Camps",
    body:
      "End-to-end life-support for camps and forward operating bases. Catering, potable water, waste, power, transport, and camp management delivered on-schedule in demanding theatres.",
    img: "https://images.pexels.com/photos/10406140/pexels-photo-10406140.jpeg?auto=compress&cs=tinysrgb&w=1400",
    bullets: ["Catering & potable water", "Camp management", "Ground transport", "Fuel & power"],
  },
  {
    n: "03",
    key: "industrial",
    icon: Building2,
    title: "Industrial Supply & Trade",
    tag: "Oil & Gas · Mining · MRO",
    body:
      "Import and general supply for oil & gas, mining, infrastructure and MRO — from chemical pumps and conveyor belts to expansion joints and rail components — via our Jordan partner.",
    img: "https://images.pexels.com/photos/35072821/pexels-photo-35072821.jpeg?auto=compress&cs=tinysrgb&w=1400",
    bullets: ["Chemical & fuel pumps", "Conveyor belts & pulleys", "Bearings, motors & filters", "PTFE & expansion joints"],
  },
  {
    n: "04",
    key: "engineering",
    icon: Wrench,
    title: "Engineering & EPC",
    tag: "Refining · Petrochemical · LNG",
    body:
      "Class-A engineering design, EPC and PMC delivered through our Beijing partnership — from crude distillation and hydrocracking to LNG plants, storage & transportation.",
    img: "https://images.pexels.com/photos/30475898/pexels-photo-30475898.jpeg?auto=compress&cs=tinysrgb&w=1400",
    bullets: ["FEED & engineering design", "EPC / PMC / IPMT", "LNG & gas processing", "Coal & natural-gas chemical"],
  },
  {
    n: "05",
    key: "aviation",
    icon: Plane,
    title: "Aviation Ground Logistics",
    tag: "International airports",
    body:
      "Integrated ground support for aircraft at international airports — fueling, catering, potable water, and urgent technical support — held to strict schedule and safety standards.",
    img: "https://images.pexels.com/photos/163792/model-planes-airplanes-miniatures-vehicles-163792.jpeg?auto=compress&cs=tinysrgb&w=1400",
    bullets: ["Aircraft fueling", "Catering & water", "Line maintenance support", "Schedule assurance"],
  },
  {
    n: "06",
    key: "ports",
    icon: Anchor,
    title: "Ports & Heavy Handling",
    tag: "Ports · Terminals",
    body:
      "High-capacity conveyor systems and terminal equipment for ports and industrial complexes. Installation, welding, rubber lining and field maintenance — one team, full accountability.",
    img: "https://images.pexels.com/photos/1427107/pexels-photo-1427107.jpeg?auto=compress&cs=tinysrgb&w=1400",
    bullets: ["Terminal conveyors", "Cold & hot vulcanising", "Pulley lagging & lining", "Field maintenance"],
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export default function Services() {
  return (
    <section
      id="services"
      className="relative py-32 md:py-40 border-t border-navy-border"
      data-testid="services"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="flex items-end justify-between flex-wrap gap-6 mb-16 md:mb-24"
        >
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-10 bg-gold" />
              <span className="overline">Capabilities</span>
            </div>
            <h2 className="h-display text-4xl md:text-6xl lg:text-7xl max-w-4xl">
              Six pillars.<br />
              <span className="text-gold">One accountable partner.</span>
            </h2>
          </div>
          <p className="font-sans text-white/60 max-w-md text-[15px] leading-[1.8]">
            Cross-cutting expertise from a single master relationship — configured
            to your operating footprint.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-navy-border">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.key}
                variants={cardVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                className="group relative border-b border-r border-navy-border p-8 md:p-10 hover:bg-navy-surface transition-colors duration-500"
                data-testid={`service-card-${s.key}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono text-[11px] tracking-[0.3em] text-white/40">
                    {s.n}
                  </span>
                  <Icon size={22} className="text-gold" strokeWidth={1.2} />
                </div>

                <div className="relative overflow-hidden aspect-[16/10] mb-8">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="absolute inset-0 w-full h-full object-cover img-graded transition-transform duration-[900ms] ease-out group-hover:scale-105"
                    style={{ clipPath: "polygon(0 0, 100% 0, 100% 92%, 92% 100%, 0 100%)" }}
                    draggable="false"
                  />
                  <div className="absolute inset-0 bg-navy-base/25 pointer-events-none" />
                </div>

                <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-gold/80 mb-3">
                  {s.tag}
                </div>
                <h3 className="h-display text-2xl md:text-[28px] leading-[1.1] mb-4">
                  {s.title}
                </h3>
                <p className="text-white/60 text-[14.5px] leading-[1.75] mb-6">{s.body}</p>

                <ul className="flex flex-col gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-white/50">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="mt-2 h-px w-4 bg-gold/70 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <span className="absolute top-0 left-0 h-px w-0 bg-gold group-hover:w-full transition-[width] duration-700" />
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
